import {AxiosInstance} from "axios";
import LeadData from "../model/lead/LeadData";
import Lead from "../model/lead/Lead";
import LeadEvent, {LeadEventType, LeadEventTypeEnum} from "../model/lead/event/LeadEvent";
import {LeadEventApiDto, UpdateLeadAssigneesApiDto, UpdateLeadStatusApiDto} from "./model/rest";
import LeadStatusEvent from "../model/lead/event/LeadStatusEvent";
import {default as LeadAppointmentEvent} from "../model/lead/event/LeadAppointmentEvent";
import LeadAppointmentStatusEvent from "../model/lead/event/LeadAppointmentStatusEvent";
import LeadAssigneeEvent from "../model/lead/event/LeadAssigneeEvent";
import LeadWatcherEvent from "../model/lead/event/LeadWatcherEvent";
import LeadQuoteFilesAttachedEvent from "../model/lead/event/LeadQuoteFilesAttachedEvent";
import LeadFilesAttachedEvent from "../model/lead/event/LeadFilesAttachedEvent";
import LeadSmsSentEvent from "../model/lead/event/LeadSmsSentEvent";
import LeadSurveyFilesAttachedEvent from "../model/lead/event/LeadSurveyFilesAttachedEvent";
import LeadIncomingSmsEvent from "../model/lead/event/LeadIncomingSmsEvent";
import LeadQuoteCommentCreatedEvent from "../model/lead/event/LeadQuoteCommentCreatedEvent";
import LeadQuoteStatusChangedEvent from "../model/lead/event/LeadQuoteStatusChangedEvent";
import LeadBDPChangedEvent from "../model/lead/event/LeadBDPChangedEvent";
import LeadAgentChangedEvent from "../model/lead/event/LeadAgentChangedEvent";
import LeadEmailSentEvent from "../model/lead/event/LeadEmailSentEvent";
import LeadExternalCallRegisteredEvent from "../model/lead/event/LeadExternalCallRegisteredEvent";
import LeadQuoteDeletedEvent from "../model/lead/event/LeadQuoteDeletedEvent";
import LeadQuoteRestoredEvent from "../model/lead/event/LeadQuoteRestoredEvent";
import CustomFieldValue from "../model/job/CustomFieldValue";
import LeadQuoteFinanceProductChangedEvent from "../model/lead/event/LeadQuoteFinanceProductChangedEvent";
import LeadFilesDeletedEvent from "../model/lead/event/LeadFilesDeletedEvent";

export default class LeadApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Creates lead
	 * @param {LeadData} data Lead form data transfer object.
	 * @returns {Promise<LeadData>} Promise containing created Lead.
	 */
	createLead(data: LeadData): Promise<Lead> {
		const url = `/api/web/leads`;
		return this.axiosInstance.post(url, data.toJson()).then(resp => Lead.fromJson(resp.data));
	}

	/**
	 * Fetch single lead
	 * @param {string} leadId Id of lead to fetch.
	 * @returns {Promise<Lead>} Promise containing fetched lead.
	 */
	fetchLead(leadId: string): Promise<Lead> {
		const url = `/api/web/leads/${leadId}`;
		return this.axiosInstance.get(url).then(resp => Lead.fromJson(resp.data));
	}

	/**
	 * Fetch single lead - waiting for the backend to prepare an endpoint (below is an example)
	 * @param {string} refNo reference number of lead to fetch.
	 * @returns {Promise<Lead>} Promise containing fetched lead.
	 */
	fetchLeadByRefNo(refNo: string): Promise<Lead> {
		const url = `/api/web/leads/reference_number/${refNo}`;
		return this.axiosInstance.get(url).then(resp => Lead.fromJson(resp.data));
	}

	/**
	 * Fetch lead events.
	 * @param {string} leadId Lead identifier to fetch events.
	 * @returns {Promise<Array<LeadEvent | LeadSoftCheckEvent | LeadStatusEvent>>} Lead events list.
	 */
	fetchLeadEvents(leadId: string): Promise<Array<LeadEventType>> {
		const url = `/api/web/leads/${leadId}/events`;
		return this.axiosInstance.get(url).then(resp => resp.data.map((event: LeadEventApiDto) => {

			switch (event.type) {
				case LeadEventTypeEnum.Created:
					return LeadEvent.fromJson(event);
				case LeadEventTypeEnum.Updated:
					return LeadEvent.fromJson(event);
				case LeadEventTypeEnum.StatusChanged:
					return LeadStatusEvent.fromJson(event);
				case LeadEventTypeEnum.AppointmentCreated:
					return LeadAppointmentEvent.fromJson(event);
				case LeadEventTypeEnum.AppointmentUpdated:
					return LeadAppointmentEvent.fromJson(event);
				case LeadEventTypeEnum.AppointmentStatusChanged:
					return LeadAppointmentStatusEvent.fromJson(event);
				case LeadEventTypeEnum.BdpAssigneeChanged:
					return LeadBDPChangedEvent.fromJson(event);
				case LeadEventTypeEnum.AgentAssigneeChanged:
					return LeadAgentChangedEvent.fromJson(event);
				case LeadEventTypeEnum.AssigneesChanged:
					return LeadAssigneeEvent.fromJson(event);
				case LeadEventTypeEnum.WatchersChanged:
					return LeadWatcherEvent.fromJson(event);
				case LeadEventTypeEnum.QuoteFilesAttached:
					return LeadQuoteFilesAttachedEvent.fromJson(event);
				case LeadEventTypeEnum.LeadFilesAttached:
					return LeadFilesAttachedEvent.fromJson(event);
				case LeadEventTypeEnum.LeadFilesDeleted:
					return LeadFilesDeletedEvent.fromJson(event);
				case LeadEventTypeEnum.SmsSent:
					return LeadSmsSentEvent.fromJson(event);
				case LeadEventTypeEnum.SurveyFilesAttached:
					return LeadSurveyFilesAttachedEvent.fromJson(event);
				case LeadEventTypeEnum.IncomingSms :
					return LeadIncomingSmsEvent.fromJson(event);
				case LeadEventTypeEnum.QuoteCommentCreated :
					return LeadQuoteCommentCreatedEvent.fromJson(event);
				case LeadEventTypeEnum.QuoteStatusChanged:
					return LeadQuoteStatusChangedEvent.fromJson(event);
				case LeadEventTypeEnum.QuoteDeleted:
					return LeadQuoteDeletedEvent.fromJson(event);
				case LeadEventTypeEnum.QuoteRestored:
					return LeadQuoteRestoredEvent.fromJson(event);
				case LeadEventTypeEnum.EmailSent:
					return LeadEmailSentEvent.fromJson(event);
				case LeadEventTypeEnum.ExternalCallRegistered:
					return LeadExternalCallRegisteredEvent.fromJson(event);
				case LeadEventTypeEnum.QuoteFinanceProductChanged:
					return LeadQuoteFinanceProductChangedEvent.fromJson(event);
				default:
					//fallback
					return null;
			}
		}).filter((item: LeadEvent) => item != null));
	}

	/**
	 * Update lead.
	 * @param {string} leadId lead identifier.
	 * @param {LeadData} data updated lead data.
	 * @return {Promise<Lead>} promise containing updated lead.
	 */
	updateLead(leadId: string, data: LeadData): Promise<Lead> {
		const url = `/api/web/leads/${leadId}`;
		return this.axiosInstance.put(url, data.toJson()).then(resp => Lead.fromJson(resp.data));
	}

	/**
	 * Update lead status.
	 * @param {string} leadId Lead id to be update.
	 * @param {UpdateLeadStatusApiDto} data Update lead status data.
	 * @return {Promise<string>} Promise with updated lead status.
	 */
	updateStatus(leadId: string, data: UpdateLeadStatusApiDto): Promise<void> {
		const url = `/api/web/leads/${leadId}/status`;
		return this.axiosInstance.put(url, data).then(() => {})
	}

	/**
	 * Update lead watchers
	 * @param {string} leadId
	 * @param {string[]} watchers
	 * @returns {Promise<void>}
	 */
	updateLeadWatchers(leadId: string, watchers: string[]): Promise<void> {
		const url = `/api/web/leads/${leadId}/watchers`;
		return this.axiosInstance.put(url, watchers).then(() => {})
	}

	/**
	 * Update lead assignees
	 * @param {string} leadId
	 * @param {UpdateLeadAssigneesApiDto} data update lead assignees data model.
	 * @returns {Promise<void>}
	 */
	updateLeadAssignees(leadId: string, data: UpdateLeadAssigneesApiDto): Promise<void> {
		const url = `/api/web/leads/${leadId}/assignees`;
		return this.axiosInstance.put(url, data).then(() => {})
	}

	/**
	 * Attach attachments to lead
	 * @param {string} leadId
	 * @param {string[]} attachmentIds
	 * @returns {Promise<any>}
	 */
	attachUploadedFiles(leadId: string, attachmentIds: string[]): Promise<any> {
		const url = `/api/web/leads/${leadId}/attachments`;
		return this.axiosInstance.post(url, {ids: attachmentIds});
	}

	/**
	 * Remove attachments from lead event
	 * @param leadId
	 * @param attachmentIds
	 */
	removeFiles(leadId: string, attachmentIds: string[]) {
		const url = `/api/web/leads/${leadId}/attachments`;
		return this.axiosInstance.delete(url, {data: attachmentIds});
	}

	/**
	 * Set tags to lead
	 * @param {string} leadId
	 * @param {string[]} tags
	 * @returns {AxiosPromise<any>}
	 */
	setTags(leadId: string, tags: string[]) {
		const url = `/api/web/leads/${leadId}/tags`;
		return this.axiosInstance.put(url, {names: tags});
	}

	/**
	 * Send SMS to lead contact.
	 * @param {string} leadId
	 * @param {string} message
	 * @returns {Promise<void>}
	 */
	sendSmsToLeadContact(leadId: string, message: string): Promise<void> {
		const url = `/api/web/leads/${leadId}/contact_send_sms`;
		return this.axiosInstance.put(url, {message: message}).then(() => {})
	}

	/**
	 * Send e-mail to lead contact.
	 * @param {string} leadId
	 * @param {string} subject
	 * @param {string} message
	 * @returns {Promise<any>}
	 */
	sendEmailToLeadContact(leadId: string, subject: string, message: string): Promise<any> {
		const url = `/api/web/leads/${leadId}/contact_send_email`;
		return this.axiosInstance.put(url, {subject, message})
	}

	/**
	 * Mention users
	 * @param {string} leadId
	 * @param userIds
	 * @param noteId
	 * @returns {AxiosPromise<any>}
	 */
	mentionUsers(leadId: string, userIds: string[], noteId?: string) {
		const url = `/api/web/leads/${leadId}/mentions`;
		return this.axiosInstance.post(url, {userIds, noteId})
	}

	/**
	 * Accept quote action
	 */
	acceptQuote(quoteId: string): Promise<string> {
		const url = `/api/web/leads/quotes/${quoteId}/accept`;
		return this.axiosInstance.put(url, {}).then(resp => resp.data.jobId);
	}

	addCallEvent(leadId: string, conversationContent: string): Promise<any> {
		const url = `/api/web/leads/${leadId}/external_calls`;
		return this.axiosInstance.post(url, {conversationContent})
	}

	/**
	 * Restore deleted quote action
	 */
	restoreQuote(quoteId: string): Promise<any> {
		const url = `/api/web/leads/quotes/${quoteId}/restore`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Fetch Lead custom fields values
	 * @param {string} leadId
	 * @returns {Promise<CustomFieldValue>}
	 */
	fetchLeadCustomFieldsValues(leadId: string): Promise<CustomFieldValue[]> {
		const url = `/api/web/leads/${leadId}/custom_field_values`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(CustomFieldValue.fromJson));
	}

	/**
	 * Create lead custom field value
	 * @param {string} leadId
	 * @param {CustomFieldValue} customFieldValue
	 * @returns {Promise<CustomFieldValue>}
	 */
	createLeadCustomFieldValue(leadId: string, customFieldValue: CustomFieldValue): Promise<CustomFieldValue> {
		const url = `/api/web/leads/${leadId}/custom_field_values`;
		return this.axiosInstance.post(url, customFieldValue.toJson()).then(resp => CustomFieldValue.fromJson(resp.data));
	}

	/**
	 * Update lead custom field value
	 * @param {CustomFieldValue} customFieldValue
	 * @returns {Promise<CustomFieldValue>}
	 */
	updateLeadCustomFieldValue(customFieldValue: CustomFieldValue): Promise<CustomFieldValue> {
		const url = `/api/web/leads/custom_field_values/${customFieldValue.id.get}`;
		return this.axiosInstance.put(url, customFieldValue.toJson()).then(resp => CustomFieldValue.fromJson(resp.data));
	}

	/**
	 * Delete lead custom field value
	 * @param {string} customFieldValueId
	 * @returns {AxiosPromise}
	 */
	deleteLeadCustomFieldValue(customFieldValueId: string) {
		const url = `/api/web/leads/custom_field_values/${customFieldValueId}`;
		return this.axiosInstance.delete(url);
	}
}